<template>
  <div class="rpilot-page-root">
    <!-- Step 1: chooser (full-width, no chat sidebar yet) -->
    <v-card v-if="currentStep === '1'" flat>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="8" lg="6">
            <div class="d-flex align-center mb-1">
              <img :src="rpilotIcon" alt="" width="40" height="40" class="mr-3 flex-shrink-0" />
              <div class="text-h6">RPilot — Schedule Assistant</div>
            </div>
            <div class="text-body-2 text-medium-emphasis mb-6">
              Pick what you'd like to do, then start the chat. RPilot can build a new
              schedule from scratch or apply bulk updates to an existing one.
            </div>

            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <v-card
                  link
                  variant="outlined"
                  class="rpilot-mode-card"
                  :class="{ 'rpilot-mode-card--selected': mode === 'create' }"
                  @click="mode = 'create'"
                >
                  <v-card-text class="d-flex align-center">
                    <v-icon
                      size="28"
                      class="mr-3"
                      :color="mode === 'create' ? 'primary' : undefined"
                    >
                      mdi-calendar-plus
                    </v-icon>
                    <div>
                      <div class="text-subtitle-2">Create new schedule</div>
                      <div class="text-caption text-medium-emphasis">
                        Build a schedule from scratch in chat
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card
                  link
                  variant="outlined"
                  class="rpilot-mode-card"
                  :class="{ 'rpilot-mode-card--selected': mode === 'update' }"
                  @click="mode = 'update'"
                >
                  <v-card-text class="d-flex align-center">
                    <v-icon
                      size="28"
                      class="mr-3"
                      :color="mode === 'update' ? 'primary' : undefined"
                    >
                      mdi-calendar-edit
                    </v-icon>
                    <div>
                      <div class="text-subtitle-2">Update existing schedule</div>
                      <div class="text-caption text-medium-emphasis">
                        Apply bulk changes to a schedule you pick
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-expand-transition>
              <div v-if="mode === 'update'">
                <v-autocomplete
                  v-model="selectedSchedule"
                  :items="sortedSchedules"
                  item-title="title"
                  :return-object="true"
                  label="Schedule"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  class="mt-2"
                />
              </div>
            </v-expand-transition>

            <v-btn
              color="primary"
              class="mt-2"
              :disabled="!canProceed"
              :loading="loadingTasks"
              @click="onNext"
            >
              Next
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Step 2: gantt + chat sidebar split. ReviewStructureStep's root is a
         v-stepper-window-item, which needs a v-window ancestor to provide the
         v-window-group injection — wrap it in a plain v-window pinned to
         stepValue so the item renders. -->
    <div v-else class="rpilot-page">
      <div class="rpilot-main">
        <v-window :model-value="'2'" class="rpilot-window">
          <ReviewStructureStep
            ref="reviewRef"
            stepValue="2"
            :nestedTasks="nestedTasks"
            :displayTasks="displayTasks"
            :changes="changes"
            :selectedSchedule="effectiveSchedule"
            :newScheduleProposal="pendingProposal?.newSchedule || null"
            :timeRangeEditability="timeRangeEditability"
            :saving="savingProposal"
            @update:changes="changes = $event"
            @update:nested-tasks="nestedTasks = $event"
            @update:time-range-calendars="onTimeRangeCalendarsChange"
            @update:editable-time-range-ids="editableTimeRangeIds = $event"
            @import="onImport"
            @back="goBack"
          />
        </v-window>

        <v-alert
          v-if="pendingProposal"
          type="info"
          variant="tonal"
          density="compact"
          class="rpilot-proposal-banner"
        >
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <strong>Proposal from RPilot:</strong>
              {{ pendingProposal.commitTitle || 'Untitled change' }}
              <span class="text-medium-emphasis ml-2">
                ({{ proposalCounts.new }} new, {{ proposalCounts.updated }} updated, {{ proposalCounts.deleted }} deleted)
              </span>
            </div>
            <v-btn
              color="primary"
              variant="flat"
              size="small"
              class="mr-1"
              @click="openCommitDialog"
            >
              Save…
            </v-btn>
            <v-btn variant="text" size="small" @click="rejectProposal">Reject</v-btn>
          </div>
        </v-alert>
      </div>

      <RPilotChatSidebar
        ref="chatRef"
        :scoped-token="settingsStore.settings.scopedToken"
        :api-url="settingsStore.settings.apiUrl"
        :project-code="settingsStore.settings.projectCode"
        :session-context="sessionContext"
        :features="{ audio: true, files: true, images: false, thinking: true, sessions: true }"
        persist-session
        @tool-call="onToolCall"
        @tool-call-cancelled="resetSession"
        @new-chat="resetSession"
      />
    </div>

    <RPilotDeploymentDialog
      v-model="deploymentDialog"
      :profile-code="pendingDeployment?.published?.code || ''"
      :profile-title="pendingDeployment?.published?.title || ''"
      :schedule-title="pendingDeployment?.scheduleTitle || ''"
      :targets="pendingDeployment?.targets || []"
      :title="pendingDeployment?.title || ''"
      :message="pendingDeployment?.message || ''"
      :deploying="deploying"
      @confirm="onConfirmDeployment"
      @cancel="cancelDeployment"
    />

    <v-snackbar v-model="showSuccessSnackbar" :timeout="3000" color="success">
      {{ successMessage }}
    </v-snackbar>
    <v-snackbar v-model="showErrorSnackbar" :timeout="8000" color="error">
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, shallowRef, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useSettingsStore } from '@/stores/settings';
import { useAppStore } from '@/stores/app';
import { useScheduleStore } from '@/stores/schedule';
import { useResourceStore } from '@/stores/resource';
import { useTaskStore } from '@/stores/task';
import { useRPilotStore } from '@/stores/rpilot';
import ReviewStructureStep from '@/components/steps/ReviewStructureStep.vue';
import RPilotDeploymentDialog from '@/components/dialogs/RPilotDeploymentDialog.vue';
import RPilotChatSidebar from '@/components/RPilotChatSidebar.vue';
import TaskScheduleValidator from '@/utils/TaskScheduleValidator';
import { hydrateAndValidate } from '@/utils/ProposalHydrator';
import { summarizeProblems } from '@/utils/ProposalProblems';
import { projectSandboxLocations } from '@/utils/LocationDisplay';
import { DEFAULT_CALENDAR_ID } from '@/utils/ExcelProcessorUtils';
import { applyTaskLocks, deriveTimeRangeEditability, indexTasksById } from '@/utils/TaskLockUtils';
import { prepareDeploymentProposal, describeApiError } from '@/utils/TaskSetProfileDeploymentProposal';
import { ScheduleImporter } from '@/utils/ScheduleImporter';
import { runTransformOnWorker } from '@/utils/TransformRunner';
import { useChatStore } from '@rpilot/chat/stores/chat.js';
import rpilotIcon from '@/assets/rpilot-icon.svg';

const settingsStore = useSettingsStore();
const appStore = useAppStore();
const scheduleStore = useScheduleStore();
const resourceStore = useResourceStore();
const taskStore = useTaskStore();
const rpilotStore = useRPilotStore();
const router = useRouter();

const currentStep = ref('1');
const mode = ref('create');
const selectedSchedule = ref(null);
const loadingTasks = ref(false);

// Proposed task tree state — feeds ReviewStructureStep + TaskScheduleValidator.
// On Update mode, originalTasks is the live schedule's task tree; nestedTasks
// starts as a copy of that tree (no diff). On Create mode, both start empty.
// When the agent calls rdrive_propose_schedule_commit, we replace nestedTasks
// with the proposed tree, re-run TaskScheduleValidator, and the gantt re-renders
// with diff coloring.
// shallowRef: these hold entire task trees (tens of thousands of objects on
// large schedules) and are only ever replaced wholesale, never edited in
// place through the ref — deep reactivity would just proxy every node.
const originalTasks = shallowRef([]);
const nestedTasks = shallowRef([]);
const displayTasks = shallowRef([]);
const changes = ref({});

// Pending proposal: when set, the agent is awaiting our respond() call.
// Apply path: the user clicks Save Tasks → onImport fires → import + respond.
// Reject path: the user clicks Reject in the banner → respond with error.
const pendingProposal = ref(null);   // { commitTitle, commitMessage, scheduleId, newSchedule, baseline, respond }
// True from Confirm in the Save Tasks dialog until the commit request settles.
const savingProposal = ref(false);
// Which time ranges the proposal's NEW tasks may edit, confirmed in the Save
// Tasks dialog. Derived from the live tasks on updates (uniform settings
// pre-select; disagreements show as 'mixed'); create proposals default to
// fully editable. Null when the proposal adds no tasks.
const timeRangeEditability = ref(null);
const editableTimeRangeIds = ref([]);

// Pending deployment proposal — separate from pendingProposal so an in-flight
// schedule commit isn't clobbered by a deployment (or vice versa).
const pendingDeployment = ref(null); // { published, targets, scheduleId, scheduleTitle, title, message, respond }
const deploymentDialog = ref(false);
const deploying = ref(false);

const showSuccessSnackbar = ref(false);
const successMessage = ref('');
const showErrorSnackbar = ref(false);
const errorMessage = ref('');

const chatRef = ref(null);
const reviewRef = ref(null);

// Banner Save… → the same commit dialog Save Tasks opens, prefilled with the
// agent's suggested commit title/message. Confirming emits @import → onImport.
const openCommitDialog = () => reviewRef.value?.openImportDialog();

const sortedSchedules = computed(() =>
  [...(scheduleStore.schedules || [])]
    .filter((s) => s.current)
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
);

const canProceed = computed(() => {
  if (loadingTasks.value) return false;
  if (mode.value === 'create') return true;
  return !!selectedSchedule.value;
});

// ReviewStructureStep needs a selectedSchedule for usesDates / template lookup.
// For Create mode we don't have one yet (the agent will pick a template); fall
// back to a stub so the gantt mounts. Once a proposal arrives with newSchedule
// metadata, the stub is replaced. A pending create wins over the loaded
// schedule: after a create the page sits on that schedule, and the agent may
// propose yet another new one.
const effectiveSchedule = computed(() => {
  if (pendingProposal.value?.newSchedule) {
    return {
      id: '__pending_new__',
      title: pendingProposal.value.newSchedule.title,
      code: pendingProposal.value.newSchedule.code,
      scheduleTemplateId: pendingProposal.value.newSchedule.scheduleTemplateId,
      timeRangeCalendars: pendingProposal.value.newSchedule.timeRangeCalendars || [],
      timeRanges: [],
    };
  }
  if (selectedSchedule.value) return selectedSchedule.value;
  return { id: '__pending_new__', title: 'New schedule', timeRanges: [] };
});

const proposalCounts = computed(() => ({
  new: countChanges('new'),
  updated: countChanges('updated'),
  deleted: countChanges('deleted'),
}));

function countChanges(type) {
  return Object.values(changes.value || {}).filter((c) => c.changeType === type).length;
}

const onNext = async () => {
  if (mode.value === 'update' && selectedSchedule.value) {
    await loadScheduleTasks(selectedSchedule.value);
  } else {
    originalTasks.value = [];
    nestedTasks.value = [];
    displayTasks.value = [];
    changes.value = {};
  }
  currentStep.value = '2';
  // No auto-prompt — the user starts the conversation. Context (mode, schedule
  // identity, project) flows to the agent via the sessionContext prop on
  // <RPilotChat>, which is included in the session-create payload.
};

const sessionContext = computed(() => {
  if (currentStep.value !== '2') return null;
  const projectName = settingsStore.settings.projectName || null;
  const projectCode = settingsStore.settings.projectCode || null;
  if (mode.value === 'create') {
    return { mode: 'create', projectName, projectCode };
  }
  const s = selectedSchedule.value;
  if (!s) return { mode: 'update', projectName, projectCode };
  return {
    mode: 'update',
    projectName,
    projectCode,
    schedule: { id: s.id, title: s.title, code: s.code || null },
  };
});

const loadScheduleTasks = async (schedule) => {
  loadingTasks.value = true;
  try {
    const path = `/api/${settingsStore.settings.projectCode}/tasks?scheduleIds=${schedule.id}&nested=true`;
    const tasks = await appStore.apiClient.get(path);
    const tree = Array.isArray(tasks) ? tasks : [];
    originalTasks.value = tree;
    nestedTasks.value = JSON.parse(JSON.stringify(tree));   // deep clone — diff baseline
    displayTasks.value = JSON.parse(JSON.stringify(tree));
    changes.value = {};
    taskStore.setTasks(tree);
  } catch (err) {
    console.error('[rpilot] failed to load schedule tasks', err);
  } finally {
    loadingTasks.value = false;
  }
};

const goBack = () => {
  currentStep.value = '1';
  // Reject any in-flight proposal so the agent doesn't hang.
  if (pendingProposal.value) rejectProposal();
  if (pendingDeployment.value) cancelDeployment();
};

// Agent-driven arrival via rdrive_switch_view from the Task Set Profiles view:
// consume the handoff and land directly in the chat view, so the session
// (carried across the navigation by persist-session) continues without the
// user re-doing the chooser. An unknown scheduleId falls back to create mode —
// the agent learns the schedule wasn't found from its next search, and the
// user still gets a working chat rather than a dead chooser.
onMounted(async () => {
  const handoff = rpilotStore.takeHandoff('schedules');
  if (!handoff) return;
  const schedule = handoff.scheduleId
    ? (scheduleStore.schedules || []).find((s) => s.id === handoff.scheduleId && s.current) || null
    : null;
  if (schedule) {
    mode.value = 'update';
    selectedSchedule.value = schedule;
    await loadScheduleTasks(schedule);
  } else {
    mode.value = 'create';
  }
  currentStep.value = '2';
});

// New chat: settle any dangling proposal (the abandoned session can never
// answer it, and rejectProposal also rolls the gantt back to baseline), but
// stay where the user is — a new conversation, not a new workflow. Mode,
// selected schedule and loaded tasks are kept; the next session picks them up
// via the sessionContext prop, and the Back button remains the way to return
// to the chooser.
const resetSession = () => {
  if (pendingProposal.value) rejectProposal();
  if (pendingDeployment.value) cancelDeployment();
};

// ── Sandbox payload plumbing ─────────────────────────────────────────────
// The harness inlines the agent's dataHandle/contextHandle into the tool
// arguments as `data` / `context` (the same expansion tasksHandle already
// gets), and the chat store hands them to us already JSON.parsed. The sandbox
// wants strings — only strings cross to the worker — so re-serialise here.
// A payload we can't serialise is dropped rather than failing the whole call:
// the script then sees `data === null` and can say so itself.
const serializeSandboxArg = (value, label) => {
  if (value === undefined || value === null) return undefined;
  try {
    return JSON.stringify(value);
  } catch (err) {
    console.error(`[rpilot] failed to serialise the ${label} payload`, err);
    return undefined;
  }
};

// The dashboard's own cached reference data, projected down to the fields a
// transform actually needs, so a script can resolve titles → ids itself
// instead of the agent typing ids it has only seen in a preview. Built fresh
// per call (the stores are live) and serialised once.
const buildSandboxRefsJson = (scheduleTemplate) => {
  const locations = projectSandboxLocations(resourceStore.getLocations, resourceStore.getDrawingSets);
  // RDrive teams carry `name`, not `title`; expose both so a script can use
  // either without a lookup table of our field names.
  const teams = (resourceStore.getTeams || []).map((team) => ({
    id: team.id,
    title: team.name ?? team.title ?? '',
    name: team.name ?? team.title ?? '',
    code: team.code ?? '',
  }));
  return serializeSandboxArg({ locations, teams, scheduleTemplate: scheduleTemplate || null }, 'reference data');
};

// The loaded schedule's template (with its timeRangeTemplates) — the hydrator
// validates against it and the sandbox gets it as the `scheduleTemplate` global.
const loadedScheduleTemplate = () => {
  const templateId = selectedSchedule.value?.scheduleTemplateId || null;
  return templateId ? (scheduleStore.scheduleTemplatesObj?.[templateId]?.[0] || null) : null;
};

// Total nodes in a nested tree — what "taskCount" means to the agent. The
// top-level length would report 3 for a 57k-task schedule.
const countTasks = (tasks) => (tasks || []).reduce(
  (total, task) => total + 1 + countTasks(task.children), 0,
);

// The Save result's account of ids the dashboard assigned to new tasks in
// place of the agent's placeholders. Capped: a transform that stamps
// thousands of placeholder ids must not push a megabyte of pairs into the
// model's context — past the cap the agent is told to reload the tree.
const TASK_ID_MAP_LIMIT = 200;
const describeAssignedTaskIds = (taskIdMap) => {
  const entries = Object.entries(taskIdMap || {});
  if (entries.length === 0) return {};
  const truncated = entries.length > TASK_ID_MAP_LIMIT;
  return {
    taskIdMap: Object.fromEntries(truncated ? entries.slice(0, TASK_ID_MAP_LIMIT) : entries),
    taskIdMapNote: 'New tasks never keep a proposed id: the dashboard assigned fresh ids and remapped '
      + 'prerequisiteTaskIds accordingly. taskIdMap is { yourProposedId: assignedId }'
      + (truncated
        ? ` for the first ${TASK_ID_MAP_LIMIT} of ${entries.length} replaced ids — reload the committed tree for the rest.`
        : '.'),
  };
};

// ── Frontend-tool dispatch ───────────────────────────────────────────────

const onToolCall = ({ name, args, respond, setStatus }) => {
  if (name === 'rdrive_propose_schedule_commit') {
    handleProposeCommit(args, respond, setStatus);
    return;
  }
  if (name === 'rdrive_propose_schedule_transform') {
    handleProposeScheduleTransform(args, respond, setStatus);
    return;
  }
  if (name === 'rdrive_inspect_loaded_schedule') {
    handleInspectLoadedSchedule(args, respond);
    return;
  }
  if (name === 'rdrive_propose_task_set_profile_deployment') {
    handleProposeDeployment(args, respond, setStatus);
    return;
  }
  if (name === 'rdrive_switch_view') {
    handleSwitchView(args, respond);
    return;
  }
  if (name === 'rdrive_propose_task_set_profile_commit') {
    respond({
      error:
        'This RPilot view manages schedules, not task set profiles — the proposal was not shown. ' +
        'Call rdrive_switch_view({ view: "task-set-profiles" }) to move the dashboard to the ' +
        'Task Set Profiles view, then re-propose there.'
    });
    return;
  }
  // Default agent's rdrive_pick_location is not declared on this agent,
  // but be defensive — anything we don't handle gets a clean error so the
  // turn doesn't hang.
  respond({ error: `Unknown frontend tool: ${name}` });
};

// Agent-driven navigation to the Task Set Profiles RPilot view. Respond
// BEFORE navigating: the respond closure and the chat stores outlive this
// page (shared Pinia), so the resumed stream keeps flowing while the other
// page mounts and — via persist-session — picks the transcript straight up.
const handleSwitchView = (args, respond) => {
  const view = args?.view;
  if (view === 'schedules') {
    respond({ result: JSON.stringify({ switched: false, view, note: 'Already on the Schedules view.' }) });
    return;
  }
  if (view !== 'task-set-profiles') {
    respond({ error: `Unknown view "${view}" — use "schedules" or "task-set-profiles".` });
    return;
  }
  // An unanswered proposal would hang the agent forever once this page
  // unmounts (its respond is bound to page state the user can no longer act
  // on) — settle it first.
  if (pendingProposal.value || pendingDeployment.value) {
    respond({
      error: 'A proposal on the Schedules view is still awaiting the user — ' +
        'wait for its outcome (or ask the user to resolve it) before switching views.'
    });
    return;
  }
  rpilotStore.setHandoff({
    view,
    mode: args?.mode || null,
    scheduleId: null,
    taskSetProfileId: args?.taskSetProfileId || null,
  });
  respond({ result: JSON.stringify({ switched: true, view }) });
  router.push({ name: 'TSPRPilot' });
};

const handleProposeCommit = async (args, respond, setStatus) => {
  // The harness inlines tasksHandle into tasks before the call reaches us. If
  // the handle survives, the server predates that expansion — fail cleanly
  // rather than previewing an empty tree.
  if (args?.tasksHandle && !Array.isArray(args?.tasks)) {
    respond({
      error:
        'tasksHandle was not expanded by the RPilot server (harness out of date). ' +
        'Propose with inline tasks for now.'
    });
    return;
  }
  const proposedTree = Array.isArray(args?.tasks) ? args.tasks : [];

  if (mode.value === 'update' && selectedSchedule.value && args?.scheduleId
      && args.scheduleId !== selectedSchedule.value.id) {
    respond({
      error: `Proposal targets scheduleId "${args.scheduleId}" but the user is editing `
        + `"${selectedSchedule.value.title}" (${selectedSchedule.value.id}) — propose against the loaded schedule.`
    });
    return;
  }

  // A proposal creates a schedule when it names no scheduleId and either no
  // schedule is loaded or it carries newSchedule. The second case matters after
  // a create: the page then sits on the schedule it just made, and a further
  // newSchedule proposal must not be previewed (or hydrated) as an edit of it.
  const isCreate = !args?.scheduleId && (!selectedSchedule.value || !!args?.newSchedule);

  const templateId = isCreate
    ? args?.newSchedule?.scheduleTemplateId || null
    : selectedSchedule.value?.scheduleTemplateId || args?.newSchedule?.scheduleTemplateId || null;
  const scheduleTemplate = templateId
    ? (scheduleStore.scheduleTemplatesObj?.[templateId]?.[0] || null)
    : null;

  let newScheduleCalendars = null;
  if (isCreate) {
    const newSchedule = args?.newSchedule;
    const missing = ['title', 'code', 'scheduleTemplateId'].filter((field) => !newSchedule?.[field]);
    if (missing.length > 0) {
      respond({ error: `Proposal rejected: newSchedule is missing ${missing.map((f) => `"${f}"`).join(', ')}.` });
      return;
    }
    if (!scheduleTemplate && Object.keys(scheduleStore.scheduleTemplatesObj || {}).length > 0) {
      respond({
        error: `Proposal rejected: newSchedule.scheduleTemplateId "${newSchedule.scheduleTemplateId}" does not `
          + 'match any schedule template in this project — use rdrive_search_schedule_templates.'
      });
      return;
    }
    const resolved = resolveNewScheduleCalendars(newSchedule.timeRangeCalendars, scheduleTemplate);
    if (resolved.errors.length > 0) {
      respond({
        error: 'Proposal rejected — fix newSchedule.timeRangeCalendars and re-propose:\n- '
          + resolved.errors.join('\n- ')
      });
      return;
    }
    newScheduleCalendars = resolved.mapping;
  }

  await applyProposedTree(proposedTree, {
    respond,
    setStatus,
    // On create there is no schedule yet — a synthetic one carries the
    // resolved per-time-range calendar mapping so tracking rows resolve to
    // the agent's (user-confirmed) calendars instead of the seed fallback.
    schedule: isCreate
      ? (newScheduleCalendars ? { timeRangeCalendars: newScheduleCalendars } : null)
      : selectedSchedule.value,
    scheduleTemplate,
    // A new schedule starts empty: diffing against the loaded one would show
    // its tasks as deleted and keep their ids, moving them on commit.
    baseline: isCreate ? [] : originalTasks.value,
    scheduleId: args?.scheduleId || null,
    newSchedule: isCreate
      ? { ...args.newSchedule, timeRangeCalendars: newScheduleCalendars }
      : null,
    commitTitle: args?.commitTitle || '',
    commitMessage: args?.commitMessage || '',
    // Deterministic chat summary built from the proposal itself — the model
    // doesn't reliably write prose alongside the tool call.
    summarize: (changesMap) => postProposalSummaryToChat(args, changesMap),
  });
};

// Shared downstream for every proposed tree (agent commit OR browser
// transform): hydrate mechanical fields → run the diff validator → paint the
// gantt → seed the editable-time-range confirmation → arm pendingProposal so
// the user's Save/Reject drives the same onImport/rejectProposal path. Returns
// true when the proposal is now awaiting the user, false when it was rejected
// by validation (respond has already been called with the error either way).
const applyProposedTree = async (proposedTree, {
  respond,
  setStatus = null,
  schedule,
  scheduleTemplate,
  // The tree the proposal is diffed against: the loaded schedule's live tasks
  // for an edit, empty for a new schedule.
  baseline = originalTasks.value,
  scheduleId = null,
  newSchedule = null,
  commitTitle = '',
  commitMessage = '',
  summarize,
  logs = null,
}) => {
  const locations = resourceStore.getLocations || [];

  // Hydrate mechanical fields (ids, parent/child links, subtask bookkeeping,
  // time-range rows/calendars) and validate what can't be hydrated. Errors go
  // straight back to the agent; the gantt keeps its previous state.
  const validation = hydrateAndValidate(proposedTree, {
    originalTasks: baseline,
    schedule,
    scheduleTemplate,
    itemTemplates: taskStore.itemTemplates || [],
    teams: resourceStore.getTeams || [],
    locations,
  });
  const hydratedTree = validation.tasks;
  if (validation.errors.length > 0) {
    // Grouped by kind, with exact counts and one actionable hint, capped at
    // ~2 KB — a bulk failure has to read as "542 of 585 ids are unknown", not
    // as 30 near-identical strings the model will try to fix one by one. The
    // structured fields ride along so it can branch on the counts.
    const summary = summarizeProblems(validation, {
      headline: 'Proposal rejected by dashboard validation — nothing was shown to the user. '
        + 'Fix these and re-propose.',
    });
    respond({
      payload: {
        error: summary.error,
        problemCounts: summary.problemCounts,
        samples: summary.samples,
        ...(logs ? { logs } : {}),
      },
    });
    return false;
  }

  // Run the same validator the spreadsheet flow uses — produces the
  // changes map that ReviewStructureStep colors the gantt with.
  // validate() is async (time-sliced diff on large schedules).
  let validationResult;
  try {
    // validate() returns a separate display tree with the deleted originals
    // put back (markedForDeletion) so the gantt shows them red; hydratedTree
    // itself is left untouched, so it stays safe to commit — those reinserted
    // rows would otherwise upsert the "deleted" tasks right back.
    const validator = new TaskScheduleValidator(baseline, hydratedTree, locations);
    validationResult = await validator.validate();
  } catch (err) {
    console.error('[rpilot] proposal validation failed', err);
    respond({ error: `Proposal validation failed: ${err.message || err}` });
    return false;
  }

  nestedTasks.value = hydratedTree;
  displayTasks.value = validationResult.tasks;
  changes.value = validationResult.changes;

  // Seed the Save Tasks dialog's editable-time-ranges confirmation when the
  // proposal introduces tasks the schedule doesn't have yet.
  const originalById = indexTasksById(baseline);
  const addsTasks = (tasks) => (tasks || []).some(
    (task) => !originalById.has(task.id) || addsTasks(task.children),
  );
  const rangeTemplates = scheduleTemplate?.timeRangeTemplates || [];
  timeRangeEditability.value = addsTasks(hydratedTree) && rangeTemplates.length > 0
    ? deriveTimeRangeEditability(baseline, rangeTemplates)
    : null;

  // Pre-populate the suggested commit title/message into the scheduleStore so
  // the commit dialog shows them as editable defaults. Unconditional — a
  // proposal without a message must not inherit the previous proposal's text.
  scheduleStore.updateCommitTitle(commitTitle || '');
  scheduleStore.updateCommitMessage(commitMessage || '');

  if (typeof summarize === 'function') summarize(validationResult.changes);

  pendingProposal.value = {
    scheduleId,
    newSchedule,
    baseline,
    commitTitle: commitTitle || '',
    commitMessage: commitMessage || '',
    respond,
    // Ids the agent proposed for NEW tasks were replaced with minted ones (see
    // ProposalHydrator); the agent gets the proposed→assigned pairs back on
    // Save so it can address those tasks (e.g. deployment parentTaskIds).
    taskIdMap: validation.taskIdMap || {},
    // A transform's sandbox logs have to outlive the run: the agent only gets
    // its answer when the user saves or rejects, and the tool contract
    // promises `logs` on both.
    logs,
  };
  // The chat's loader keeps running while the turn waits on the user — tell
  // it what it is waiting for instead of the tool's "Proposing…" summary.
  setStatus?.('Review the proposal in the Gantt, then Save Tasks or Reject');
  return true;
};

// ── Browser-side transform proposals ─────────────────────────────────────
// rdrive_propose_schedule_transform ships JS that the QuickJS-wasm sandbox
// runs against the LOADED tree (the user's working copy, unsaved edits and
// all). The transformed array feeds the very same applyProposedTree pipeline a
// commit proposal uses — so validation, diffing, the gantt preview and the
// Save/Reject gate are identical; only the source of the tree differs. The
// tree never leaves the browser and the script gets no page authority.
const handleProposeScheduleTransform = async (args, respond, setStatus) => {
  if (currentStep.value !== '2' || (mode.value !== 'update') || !selectedSchedule.value
      || !Array.isArray(originalTasks.value) || originalTasks.value.length === 0) {
    respond({
      error: 'No schedule is loaded in the dashboard to transform. Ask the user to open a schedule '
        + '(Update an existing schedule), or use rdrive_propose_schedule_commit to create new structure.'
    });
    return;
  }
  if (args?.scheduleId && args.scheduleId !== selectedSchedule.value.id) {
    respond({
      error: `Transform targets scheduleId "${args.scheduleId}" but the user is editing `
        + `"${selectedSchedule.value.title}" (${selectedSchedule.value.id}). Transform the loaded schedule, `
        + 'or omit scheduleId.'
    });
    return;
  }
  if (typeof args?.code !== 'string' || args.code.trim() === '') {
    respond({ error: 'Transform rejected: "code" (the JavaScript to run) is required.' });
    return;
  }

  // Note there is no whitelist on args: unknown fields are ignored, never
  // rejected, so a harness that starts inlining a payload this dashboard build
  // doesn't know about yet degrades to "that global is null" instead of
  // failing the call.
  const scheduleTemplate = loadedScheduleTemplate();

  let run;
  try {
    // Transform the WORKING copy — what the user currently sees, including any
    // unsaved edits — not the pristine baseline. deepToRaw + JSON happens
    // inside the runner, off a Vue proxy the worker could never structured-clone.
    // `data` / `context` are the agent's own payloads (harness-expanded from
    // dataHandle / contextHandle); locations / teams / scheduleTemplate come
    // from the dashboard's caches so the script can resolve ids itself.
    run = await runTransformOnWorker({
      code: args.code,
      tasks: nestedTasks.value,
      dataJson: serializeSandboxArg(args?.data, 'data'),
      contextJson: serializeSandboxArg(args?.context, 'context'),
      refsJson: buildSandboxRefsJson(scheduleTemplate),
    });
  } catch (err) {
    console.error('[rpilot] transform run failed', err);
    respond({ error: `Transform failed to run: ${err?.message || err}` });
    return;
  }

  if (!run.ok) {
    // Hand the structured sandbox error + captured logs straight back to the
    // agent so it can fix the script and re-propose. Nothing was shown.
    respond({
      payload: {
        error: `Transform failed (${run.error?.kind || 'error'}): ${run.error?.message || 'Unknown error'}`,
        logs: run.logs || [],
      },
    });
    return;
  }

  const proposedTree = Array.isArray(run.result) ? run.result : [];

  const armed = await applyProposedTree(proposedTree, {
    respond,
    setStatus,
    logs: run.logs || [],
    schedule: selectedSchedule.value,
    scheduleTemplate,
    scheduleId: selectedSchedule.value.id,
    newSchedule: null,
    // The agent's one-line summary doubles as the editable commit-title default.
    commitTitle: (typeof args?.summary === 'string' && args.summary.trim()) ? args.summary.trim() : '',
    commitMessage: '',
    summarize: (changesMap) => postTransformSummaryToChat(args, run, changesMap),
  });

  // applyProposedTree has either armed pendingProposal (respond fires later on
  // Save/Reject) or already responded with a validation error. When it armed
  // the proposal, we do NOT respond here — the user's Save/Reject does, exactly
  // as for a commit proposal.
  if (!armed) return;
};

// Chat bubble for a transform proposal — mirrors postProposalSummaryToChat but
// leads with the transform's one-line summary and surfaces its log tail so the
// user sees what the script reported it changed.
const postTransformSummaryToChat = (args, run, changesMap) => {
  const chatStore = useChatStore();
  const counts = Object.values(changesMap || {}).reduce((acc, c) => {
    if (acc[c.changeType] !== undefined) acc[c.changeType] += 1;
    return acc;
  }, { new: 0, updated: 0, deleted: 0 });

  const summary = (typeof args?.summary === 'string' && args.summary.trim())
    ? args.summary.trim() : 'Transform';
  const lines = [`**Proposal — ${summary}**`];
  const logs = (run?.logs || []).filter(Boolean);
  if (logs.length > 0) lines.push(logs.slice(-3).map((l) => `\`${l}\``).join('\n'));
  lines.push(
    `${counts.new} new · ${counts.updated} updated · ${counts.deleted} deleted — ` +
    'review the highlighted Gantt preview, then Save Tasks to apply or Reject.'
  );
  chatStore.messages.push({ role: 'assistant', content: lines.join('\n\n') });
};

// ── Browser-side read-only inspection ────────────────────────────────────
// rdrive_inspect_loaded_schedule is the read-only sibling of the transform:
// same QuickJS-wasm sandbox, same `tasks` global (the loaded working tree),
// but the script's `result` — any JSON value: counts, samples, key lists —
// comes straight back to the agent as the tool result. Nothing enters the
// proposal pipeline: no hydration, no diff, no gantt change, no user approval,
// and no store/state mutation. Because the payload lands in the chat context
// verbatim, the result is hard-capped well below the transform's cap.
const INSPECT_RESULT_CAP_BYTES = 16 * 1024;

const handleInspectLoadedSchedule = async (args, respond) => {
  if (currentStep.value !== '2' || (mode.value !== 'update') || !selectedSchedule.value
      || !Array.isArray(originalTasks.value) || originalTasks.value.length === 0) {
    respond({
      error: 'No schedule is loaded in the dashboard to inspect. Ask the user to open a schedule '
        + '(Update an existing schedule) first.'
    });
    return;
  }
  if (args?.scheduleId && args.scheduleId !== selectedSchedule.value.id) {
    respond({
      error: `Inspection targets scheduleId "${args.scheduleId}" but the user is editing `
        + `"${selectedSchedule.value.title}" (${selectedSchedule.value.id}). Inspect the loaded schedule, `
        + 'or omit scheduleId.'
    });
    return;
  }
  if (typeof args?.code !== 'string' || args.code.trim() === '') {
    respond({ error: 'Inspection rejected: "code" (the JavaScript to run) is required.' });
    return;
  }

  let run;
  try {
    // Same raw clone of the WORKING copy the transform sees (deepToRaw + JSON
    // inside the runner) — the script can never touch the live reactive tree.
    // Same payload globals as the transform, so "how many of these 585 titles
    // exist in the project?" is one call instead of a guess.
    run = await runTransformOnWorker({
      code: args.code,
      tasks: nestedTasks.value,
      dataJson: serializeSandboxArg(args?.data, 'data'),
      contextJson: serializeSandboxArg(args?.context, 'context'),
      refsJson: buildSandboxRefsJson(loadedScheduleTemplate()),
      options: { requireArrayResult: false, maxResultBytes: INSPECT_RESULT_CAP_BYTES },
    });
  } catch (err) {
    console.error('[rpilot] inspection run failed', err);
    respond({ error: `Inspection failed to run: ${err?.message || err}` });
    return;
  }

  if (!run.ok) {
    if (run.error?.kind === 'result_too_large') {
      // Structured cap error: state the actual size and steer the agent toward
      // aggregate answers instead of dumping task arrays.
      respond({
        payload: {
          error: `Inspection result too large: ${run.error.bytes} bytes, over the `
            + `${INSPECT_RESULT_CAP_BYTES / 1024} KB cap for inspection results. Do not return task arrays — `
            + 'return counts, small samples and key lists (e.g. { taskCount, depth, sampleTask, keys }).',
          logs: run.logs || [],
        },
      });
      return;
    }
    respond({
      payload: {
        error: `Inspection failed (${run.error?.kind || 'error'}): ${run.error?.message || 'Unknown error'}`,
        logs: run.logs || [],
      },
    });
    return;
  }

  // Pure read: hand the JSON result straight back to the agent, with logs —
  // exactly the { ok, result, logs } shape the tool description promises.
  respond({ payload: { ok: true, result: run.result, logs: run.logs || [] } });
};

// Validate + complete a create-proposal's per-time-range calendar mapping:
// one entry per tracking time-range template on the chosen schedule template.
// Agent-supplied entries win; ranges the agent skipped fall back to the seed
// calendar when it exists in this project, else the first published calendar —
// mirroring what the hydrator would otherwise stamp, so the review UI shows
// the truth. Returns { mapping, errors }; mapping is null for 'none'-only
// templates or when the project has no published calendars.
const resolveNewScheduleCalendars = (agentEntries, scheduleTemplate) => {
  const errors = [];
  const calendars = scheduleStore.getCalendars || [];
  const calendarsById = new Map(calendars.map((cal) => [cal.id, cal]));
  const rangeTemplates = scheduleTemplate?.timeRangeTemplates || [];
  const trackingTemplates = rangeTemplates.filter((template) => template.type !== 'none');

  const entries = Array.isArray(agentEntries) ? agentEntries : [];
  for (const entry of entries) {
    if (!rangeTemplates.some((template) => template.id === entry?.timeRangeTemplateId)) {
      errors.push(`timeRangeCalendars references unknown timeRangeTemplateId "${entry?.timeRangeTemplateId}" — `
        + `the chosen template declares: ${rangeTemplates.map((t) => `${t.id} (${t.title})`).join(', ') || 'none'}.`);
    } else if (!calendarsById.has(entry?.calendarId)) {
      errors.push(`timeRangeCalendars references unknown calendarId "${entry?.calendarId}" — `
        + 'use rdrive_search_calendars to find published calendar ids.');
    }
  }
  if (errors.length > 0 || trackingTemplates.length === 0) {
    return { mapping: null, errors };
  }

  const fallback = calendarsById.get(DEFAULT_CALENDAR_ID) || calendars[0] || null;
  const mapping = [];
  for (const template of trackingTemplates) {
    const entry = entries.find((e) => e.timeRangeTemplateId === template.id);
    const calendar = entry ? calendarsById.get(entry.calendarId) : fallback;
    if (!calendar) continue;
    mapping.push({
      timeRangeTemplateId: template.id,
      calendarId: calendar.id,
      calendarVersionReference: calendar.versionReference ?? null,
    });
  }
  return { mapping: mapping.length > 0 ? mapping : null, errors };
};

// Review-step calendar selector changed — keep the pending create proposal's
// mapping in sync so the POST /schedules body matches what the user sees.
const onTimeRangeCalendarsChange = (mapping) => {
  if (!pendingProposal.value?.newSchedule) return;
  pendingProposal.value = {
    ...pendingProposal.value,
    newSchedule: { ...pendingProposal.value.newSchedule, timeRangeCalendars: mapping },
  };
};

// Push a summary of the incoming proposal into the chat transcript. The
// dashboard shares the widget's Pinia, so this lands as a normal assistant
// bubble right where the user is looking while the proposal awaits review.
const postProposalSummaryToChat = (args, changesMap) => {
  const chatStore = useChatStore();
  const counts = Object.values(changesMap || {}).reduce((acc, c) => {
    if (acc[c.changeType] !== undefined) acc[c.changeType] += 1;
    return acc;
  }, { new: 0, updated: 0, deleted: 0 });

  const heading = args?.newSchedule
    ? `**Proposal — new schedule "${args.newSchedule.title}"** (code \`${args.newSchedule.code}\`)`
    : `**Proposal${args?.commitTitle ? ` — ${args.commitTitle}` : ''}**`;

  const lines = [heading];
  if (args?.newSchedule && args?.commitTitle) lines.push(args.commitTitle);
  if (args?.commitMessage) lines.push(args.commitMessage);
  lines.push(
    `${counts.new} new · ${counts.updated} updated · ${counts.deleted} deleted — ` +
    'review the highlighted Gantt preview, then Save Tasks to apply or Reject.'
  );
  chatStore.messages.push({ role: 'assistant', content: lines.join('\n\n') });
};

// Triggered by ReviewStructureStep's Save Tasks → confirm dialog → @import.
const onImport = async () => {
  if (!pendingProposal.value) {
    // Manual save without an agent proposal — fall through to normal import.
    return;
  }

  if (savingProposal.value) return; // Confirm is disabled, but guard a double fire.

  const proposal = pendingProposal.value;
  let createdSchedule = null;
  savingProposal.value = true;
  try {
    const importer = new ScheduleImporter(appStore.apiClient, settingsStore.settings);
    const commitTitle = scheduleStore.getCommitTitle || proposal.commitTitle
      || (proposal.newSchedule ? 'Created via RPilot' : 'Updated via RPilot');
    const commitMessage = scheduleStore.getCommitMessage || proposal.commitMessage || '';

    let scheduleId = proposal.scheduleId;
    if (!scheduleId && proposal.newSchedule) {
      // Two-step create, same as CreateSchedulePage and the backend contract:
      // POST the schedule shell first (there is no POST /schedules/commits —
      // that path 405s), then commit the task tree to /schedules/{id}/commits.
      // Same body CreateSchedulePage posts — entries without a resolved
      // calendar version are dropped because the API rejects nulls.
      const timeRangeCalendars = (proposal.newSchedule.timeRangeCalendars || [])
        .filter((m) => m.calendarId && m.calendarVersionReference);
      createdSchedule = await appStore.apiClient.post(
        `/api/${settingsStore.settings.projectCode}/schedules`,
        {
          title: proposal.newSchedule.title,
          code: proposal.newSchedule.code,
          scheduleTemplateId: proposal.newSchedule.scheduleTemplateId,
          ...(timeRangeCalendars.length > 0 ? { timeRangeCalendars } : {}),
          current: true,
        },
      );
      scheduleId = createdSchedule?.id || null;
      if (!scheduleId) throw new Error('Schedule creation returned no id');
      scheduleStore.addSchedule(createdSchedule);
    }

    // Every task in a commit belongs to the target schedule by definition —
    // stamp the tree so agent-invented placeholder scheduleIds (unavoidable on
    // create: the id doesn't exist until the shell is made) can't fail the
    // server's per-task lookup ("Task X: Schedule not found").
    const stampScheduleId = (tasks) => tasks.map((task) => ({
      ...task,
      scheduleId,
      subtasks: Array.isArray(task.subtasks)
        ? task.subtasks.map((subtask) => ({ ...subtask, scheduleId }))
        : task.subtasks,
      children: Array.isArray(task.children) ? stampScheduleId(task.children) : task.children,
    }));

    // Stamp locks before commit: existing tasks echo their live lock, new
    // tasks get the editable time ranges confirmed in the Save Tasks dialog
    // (a commit without editableTimeRangeTemplateIds locks every range).
    applyTaskLocks(nestedTasks.value, indexTasksById(proposal.baseline), editableTimeRangeIds.value);

    await importer.importTasks({
      scheduleId,
      tasks: stampScheduleId(nestedTasks.value),
      commitTitle,
      commitMessage,
      taskSetsPayload: {},
    });

    const taskCount = countTasks(nestedTasks.value);
    proposal.respond({
      payload: {
        ok: true,
        applied: true,
        scheduleId,
        // Every node in the tree, not the top-level row count.
        taskCount,
        ...describeAssignedTaskIds(proposal.taskIdMap),
        ...(proposal.logs ? { logs: proposal.logs } : {}),
      },
    });
    successMessage.value = createdSchedule
      ? `Created ${createdSchedule.title || createdSchedule.code} with ${taskCount} task${taskCount !== 1 ? 's' : ''}`
      : `Saved ${taskCount} task${taskCount !== 1 ? 's' : ''}`;
    showSuccessSnackbar.value = true;

    // Clear pending state and re-baseline so subsequent diffs are against the
    // new state. After a create, continue the session against the new schedule.
    if (createdSchedule) {
      selectedSchedule.value = createdSchedule;
      mode.value = 'update';
    }
    originalTasks.value = JSON.parse(JSON.stringify(nestedTasks.value));
    changes.value = {};
    pendingProposal.value = null;
    timeRangeEditability.value = null;
    scheduleStore.updateCommitTitle('');
    scheduleStore.updateCommitMessage('');
  } catch (err) {
    console.error('[rpilot] commit failed', err);
    // err.message alone is "Request failed with status code 422" — the
    // server's per-task validation lines live in err.response.data. The agent
    // needs them to know what to fix, and the same string lands in the
    // session's audit trail as the tool result.
    const detail = describeApiError(err);
    // Schedule create + task commit is not atomic. If the shell exists but the
    // commit failed, tell the agent the id so its next proposal can target it
    // instead of re-creating (the code would clash).
    const error = createdSchedule?.id
      ? `Task commit failed after the schedule was created (scheduleId ${createdSchedule.id}, code ${createdSchedule.code}). `
        + `Propose again with scheduleId "${createdSchedule.id}" to fill it. Underlying error: ${detail}`
      : `Commit failed: ${detail}`;
    // The user needs to see the Save didn't stick too — snackbar for
    // immediacy, chat bubble so the failure (and the agent's follow-up fix)
    // reads as one thread in the transcript.
    errorMessage.value = `Commit failed — ${detail}`;
    showErrorSnackbar.value = true;
    // The shell exists now, so load it: the retry the agent is told to make
    // targets its id, which must match the loaded schedule to be accepted.
    if (createdSchedule?.id) {
      selectedSchedule.value = createdSchedule;
      mode.value = 'update';
      originalTasks.value = [];
    }
    postCommitFailureToChat(detail);
    proposal.respond(proposal.logs ? { payload: { error, logs: proposal.logs } } : { error });
    pendingProposal.value = null;
    timeRangeEditability.value = null;
  } finally {
    // The proposal is settled either way (applied, or rejected by the server
    // and handed back to the agent), so the Save Tasks dialog has nothing
    // left to confirm.
    savingProposal.value = false;
    reviewRef.value?.closeImportDialog();
  }
};

// Drop a failure marker into the chat transcript so the user sees the save
// was rejected and the agent's next message/proposal reads as the retry.
const postCommitFailureToChat = (detail) => {
  const chatStore = useChatStore();
  chatStore.messages.push({
    role: 'assistant',
    content: `⚠️ **Commit failed** — the server rejected the save:\n\n${detail}\n\nRPilot has the full error and can revise the proposal.`,
  });
};

const rejectProposal = () => {
  if (!pendingProposal.value) return;
  const rejection = 'User rejected the proposed changes';
  pendingProposal.value.respond(pendingProposal.value.logs
    ? { payload: { error: rejection, logs: pendingProposal.value.logs } }
    : { error: rejection });
  pendingProposal.value = null;
  timeRangeEditability.value = null;
  // Roll back the gantt to the original state.
  nestedTasks.value = JSON.parse(JSON.stringify(originalTasks.value));
  displayTasks.value = JSON.parse(JSON.stringify(originalTasks.value));
  changes.value = {};
};

// ── Task set profile deployment proposals ────────────────────────────────

const handleProposeDeployment = async (args, respond, setStatus) => {
  if (pendingDeployment.value) {
    respond({
      error: 'A deployment proposal is already awaiting the user — wait for its outcome before proposing another.'
    });
    return;
  }
  try {
    const result = await prepareDeploymentProposal(args, {
      apiClient: appStore.apiClient,
      projectCode: settingsStore.settings.projectCode,
      loadedScheduleId: selectedSchedule.value?.id || null,
      loadedTasks: originalTasks.value.length ? originalTasks.value : null
    });
    if (result.error) {
      respond({ error: result.error });
      return;
    }

    const schedule =
      selectedSchedule.value?.id === args.scheduleId
        ? selectedSchedule.value
        : (scheduleStore.schedules || []).find((s) => s.id === args.scheduleId) || null;

    pendingDeployment.value = {
      published: result.published,
      targets: result.targets,
      scheduleId: args.scheduleId,
      scheduleTitle: schedule?.title || args.scheduleId,
      title: args?.title || '',
      message: args?.message || '',
      respond
    };
    deploymentDialog.value = true;
    setStatus?.('Confirm or cancel the deployment');

    const chatStore = useChatStore();
    chatStore.messages.push({
      role: 'assistant',
      content:
        `**Deployment proposal — ${result.published.code} | ${result.published.title}**\n\n` +
        `Deploy under ${result.targets.length} leaf task${result.targets.length !== 1 ? 's' : ''} in ` +
        `${schedule?.title || args.scheduleId} — Confirm Deploy to apply or Cancel.`
    });
  } catch (err) {
    console.error('[rpilot] deployment proposal failed', err);
    respond({ error: `Deployment proposal failed: ${err.message || err}` });
  }
};

const onConfirmDeployment = async ({ title, message }) => {
  const deployment = pendingDeployment.value;
  if (!deployment) return;

  deploying.value = true;
  try {
    await appStore.apiClient.patch(
      `/api/${settingsStore.settings.projectCode}/task-set-profiles/${deployment.published.id}/deployments`,
      {
        title,
        message,
        // id: '' matches the proven ScheduleImporter payload shape.
        deployments: deployment.targets.map((t) => ({ id: '', parentTaskId: t.id }))
      }
    );
    deployment.respond({
      result: JSON.stringify({
        applied: true,
        taskSetProfileId: deployment.published.id,
        profileCode: deployment.published.code,
        scheduleId: deployment.scheduleId,
        deployedCount: deployment.targets.length
      })
    });
    pendingDeployment.value = null;
    deploymentDialog.value = false;
    successMessage.value = `Deployed ${deployment.published.code} to ${deployment.targets.length} task${deployment.targets.length !== 1 ? 's' : ''}`;
    showSuccessSnackbar.value = true;

    // The server generated the profile's tree under each target — re-baseline
    // the gantt against the new state if the deployed schedule is on screen.
    if (selectedSchedule.value?.id === deployment.scheduleId) {
      await loadScheduleTasks(selectedSchedule.value);
    }
  } catch (err) {
    console.error('[rpilot] deployment failed', err);
    const reason = describeApiError(err);
    deployment.respond({ error: `Deployment failed: ${reason}` });
    pendingDeployment.value = null;
    deploymentDialog.value = false;
    errorMessage.value = `Deployment failed: ${reason}`;
    showErrorSnackbar.value = true;
  } finally {
    deploying.value = false;
  }
};

const cancelDeployment = () => {
  if (!pendingDeployment.value || deploying.value) return;
  pendingDeployment.value.respond({ error: 'User rejected the proposed deployment' });
  pendingDeployment.value = null;
  deploymentDialog.value = false;
};
</script>

<style scoped>
/* Step-1 mode chooser cards. Constant 2px border so selecting doesn't shift
   the layout; selected state recolors it and tints the background. */
.rpilot-mode-card {
  height: 100%;
  border-width: 2px;
  transition: border-color 0.15s, background-color 0.15s;
}
.rpilot-mode-card--selected {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.06);
}

.rpilot-page {
  display: flex;
  height: calc(100vh - 56px - 32px); /* viewport minus app bar minus content padding */
  min-height: 480px;
  gap: 12px;
}
.rpilot-main {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  overflow: auto;
}
.rpilot-window,
.rpilot-window :deep(.v-window__container) {
  height: 100%;
}
.rpilot-proposal-banner {
  position: sticky;
  bottom: 0;
  margin: 0;
  border-radius: 0;
  z-index: 2;
}
</style>
